import { useEffect } from "react";

/**
 * Lightweight per-route document meta. Updates <title> + meta[name=description]
 * + Open Graph + Twitter + canonical on mount. Cleans up on unmount by
 * restoring the previous values, so back-navigation in an SPA doesn't leak
 * the previous page's title.
 *
 * Usage:
 *   useDocumentMeta({
 *     title: "About | Northwestern Exterior Cleaning",
 *     description: "Meet Marlowe — owner of Northwestern Exterior Cleaning…",
 *     path: "/about",
 *   });
 */
const SITE_ORIGIN = "https://northwesternexteriorcleaning.com";
const SITE_NAME = "Northwestern Exterior Cleaning";

function setOrCreateMeta(selector, attr, value) {
  if (typeof document === "undefined") return null;
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    const [, name] = selector.match(/\[(?:name|property)="([^"]+)"\]/) || [];
    if (selector.startsWith('meta[property=')) el.setAttribute("property", name);
    else el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return { el, previous, created };
}

function setOrCreateLink(rel, href) {
  if (typeof document === "undefined") return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, previous, created };
}

export default function useDocumentMeta({ title, description, path, image }) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previousTitle = document.title;
    if (title) document.title = title;

    const url = path ? `${SITE_ORIGIN}${path}` : SITE_ORIGIN;
    const ogImage = image || `${SITE_ORIGIN}/brand/logo.png`;

    const updates = [
      description && setOrCreateMeta('meta[name="description"]', "content", description),
      title && setOrCreateMeta('meta[property="og:title"]', "content", title),
      description && setOrCreateMeta('meta[property="og:description"]', "content", description),
      setOrCreateMeta('meta[property="og:type"]', "content", "website"),
      setOrCreateMeta('meta[property="og:site_name"]', "content", SITE_NAME),
      setOrCreateMeta('meta[property="og:url"]', "content", url),
      setOrCreateMeta('meta[property="og:image"]', "content", ogImage),
      setOrCreateMeta('meta[name="twitter:card"]', "content", "summary_large_image"),
      title && setOrCreateMeta('meta[name="twitter:title"]', "content", title),
      description && setOrCreateMeta('meta[name="twitter:description"]', "content", description),
      setOrCreateMeta('meta[name="twitter:image"]', "content", ogImage),
      setOrCreateLink("canonical", url),
    ].filter(Boolean);

    return () => {
      document.title = previousTitle;
      updates.forEach((u) => {
        if (!u || !u.el) return;
        if (u.created) {
          u.el.parentNode && u.el.parentNode.removeChild(u.el);
        } else if (u.previous !== null && u.previous !== undefined) {
          // restore previous attribute value
          const attr = u.el.tagName === "LINK" ? "href" : "content";
          u.el.setAttribute(attr, u.previous);
        }
      });
    };
  }, [title, description, path, image]);
}
